import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { useHelpVideos } from '@euler/app/flows/settings/functions/useHelpVideos';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { HelpVideoInfo } from '@euler/model/HelpVideoInfo';
import { BlurView } from 'expo-blur';
import { memo, useCallback } from 'react';
import {
  FlatList,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kMargin = 5;

const Cell = memo(
  ({
    video,
    width,
    height,
    style,
    onPress,
  }: {
    width: number;
    height: number;
    video: HelpVideoInfo;
    style?: StyleProp<ViewStyle>;
    onPress?: (video: HelpVideoInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(() => {
      onPress?.(video);
    }, [onPress, video]);

    return (
      <View
        css={`
          align-items: center;
          margin: ${rpx(kMargin)}px;
        `}
      >
        <TouchableOpacity
          onPress={handlePress}
          css={`
            width: ${width}px;
            height: ${height}px;
            background-color: #fff;
            border-radius: ${rpx(10)}px;
            overflow: hidden;
          `}
          style={style}
        >
          <Img
            uri={video.thumbnail}
            css={`
              flex: 1;
            `}
            resizeMode="contain"
          />
          <BlurView
            style={StyleSheet.absoluteFill}
            intensity={15}
            tint="dark"
          />
          <Center style={StyleSheet.absoluteFill}>
            <Center
              css={`
                width: ${rpx(50)}px;
                height: ${rpx(50)}px;
                background-color: #777;
                border-radius: ${rpx(25)}px;
              `}
            >
              <Icon
                name="play"
                size={rpx(26)}
                color="#eee"
                css={`
                  transform: translateX(${rpx(2)}px);
                `}
              />
            </Center>
          </Center>
          <Center
            css={`
              background-color: rgba(0, 0, 0, 0.35);
              height: ${rpx(20)}px;
              width: ${rpx(50)}px;
              position: absolute;
              left: 0px;
              bottom: 0px;
              border-top-right-radius: ${rpx(10)}px;
            `}
          >
            <Typography.Label
              light
              color="#fff"
              size={rpx(12)}
              lineHeight={rpx(16)}
            >
              {video.duration}
            </Typography.Label>
          </Center>
        </TouchableOpacity>
        <Typography.Label
          medium
          css={`
            margin-top: ${rpx(5)}px;
            margin-bottom: ${rpx(10)}px;
          `}
        >
          {video.title}
        </Typography.Label>
      </View>
    );
  },
);

export const HelpVideosScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const videos = useHelpVideos();
    const insets = useSafeAreaInsets();
    const { width } = useContainerLayout();
    const appNavigation = useAppNavigation();
    const contentContainerStyle = useScreenContentContainerStyle();

    const cappedGridWidth = Math.min(width, 730);
    const [cellWidth, cellHeight] = computeGridLayoutItemSize({
      numberOfColumns: 2,
      gridWidth: cappedGridWidth,
      gridPadding: rpx(15),
      cellMargin: rpx(kMargin),
      aspectRatio: 165 / 275,
    });

    const onCellPress = useCallback(
      (video: HelpVideoInfo) => {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: video.url,
          resizeMode: 'contain',
        });
      },
      [appNavigation],
    );

    return (
      <FlatList
        data={videos}
        numColumns={2}
        keyExtractor={x => x.title}
        contentContainerStyle={[
          {
            paddingLeft: rpx(15),
            paddingRight: rpx(15),
            paddingTop: rpx(15),
            paddingBottom: rpx(15) + insets.bottom,
          },
          contentContainerStyle,
        ]}
        renderItem={({ item }) => (
          <Cell
            width={cellWidth}
            height={cellHeight}
            video={item}
            onPress={onCellPress}
          />
        )}
      />
    );
  },
  {
    title: '帮助',
  },
);
